import React, { Fragment, memo, useRef } from 'react';
import { Box, Button } from '@mui/material';
import MemeContent from './MemeContent';
import MemeReply from './MemeReply';
import { CommentType, PostType } from '../memeverse_types';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
interface MemeProps {
  post: PostType;
  allComments?: Record<string, CommentType>;
  onReplyClick: (item: PostType | CommentType) => void;
}

const Meme: React.FC<MemeProps> = ({ post, allComments, onReplyClick }) => {
  const memeContentRef = useRef<HTMLDivElement>(null);
  const memeWrapperRef = useRef<HTMLDivElement>(null);
  const showCommentsRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isXs = useMediaQuery(theme.breakpoints.down('md'));

  const calcCommentLimit: () => number = () => {
    const defaultHeight = (window.innerHeight - (isXs ? 225 : 61));
    const wrapperHeight = memeWrapperRef?.current?.offsetHeight || defaultHeight;
    const heightPerComment = isMobile ? 170 : 157;
    const heightMoreCommentsButton =
      showCommentsRef?.current?.offsetHeight ?? 24;

    const minImageHeight = isMobile ? (isXs ? 257 : 307) : 357;

    let postHeight =
      memeContentRef?.current?.offsetHeight ?? isMobile ? 170 : 157;

    let limit =
      post && post.images && post.images.filter((val) => !!val).length > 0
        ? Math.floor(
            (wrapperHeight - minImageHeight - heightMoreCommentsButton) /
              heightPerComment
          )
        : Math.floor(
            (wrapperHeight - postHeight - heightMoreCommentsButton) /
              heightPerComment
          );
    return limit;
  };

  const commentLimit: number = calcCommentLimit();
  const mainMeme = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const navigate = useNavigate();

  const MemoizedReply = memo(function MemoizedReply({
    commentId,
    show_typing,
  }: {
    commentId: string;
    show_typing: boolean;
  }) {
    return (
      <MemeReply
        key={`comment-${commentId}`}
        parentPostOrComment={post}
        reply={{ ...allComments[commentId], show_typing: show_typing }}
        showNested={false}
        shouldLimitLength={true}
        allComments={allComments}
        onReplyClick={onReplyClick}
      />
    );
  });

  if (!post) {
    // skip post if not found
    return <></>;
  }

  return (
    <Box
      ref={mainMeme}
      className='meme'
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '100%',
        maxHeight: '100%',
        overflow: 'hidden',
        maxWidth: '100dvw',
        scrollSnapAlign: 'start',
        scrollSnapStop: 'always',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        px: { xs: 1.5, md: 0 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          maxWidth: '100dvw',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        ref={memeWrapperRef}
      >
        <div ref={memeContentRef} style={{ width: '100%' }}>
          <MemeContent
            post={post}
            shouldLimitImageHeight={true}
            commentLimit={commentLimit}
            onReplyClick={() => onReplyClick(post)}
            memeWrapperRef={mainMeme}
          />
        </div>
        {!!post.flat_comments_list &&
          !!commentLimit &&
          post.flat_comments_list.map((commentId, index) => {
            return !!allComments[commentId] &&
              index >= post.flat_comments_list.length - commentLimit ? (
              // <MemeReply
              //   key={`comment-${commentId}`}
              //   parentPostOrComment={post}
              //   reply={allComments[commentId]}
              //   showNested={false}
              //   shouldLimitLength={true}
              //   allComments={allComments}
              // /> :
              <MemoizedReply
                key={`comment-${commentId}`}
                commentId={commentId}
                show_typing={
                  index ===
                  Math.max(
                    post.comments_counts,
                    post.flat_comments_list.length
                  ) -
                    1
                }
              />
            ) : (
              // skip comment if not in allComments
              // skip comment if beyond comment limit
              <Box
                key={`comment-${commentId}`}
                className={
                  !allComments[commentId]
                    ? `not-found ${commentId}`
                    : `comment-limit-skipped ${commentId}`
                }
              >
                <Fragment key={`comment-${commentId}`} />
              </Box>
            );
          })}
        {!!post?.comments_counts && post.comments_counts > commentLimit && (
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'center',
              pt: 1,
            }}
            ref={showCommentsRef}
          >
            <Button
              variant='outlined'
              component='a'
              href={`/ask/post/${post.docId}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/ask/post/${post.docId}`, {
                  state: {
                    from: location.pathname,
                    scrollY:
                      document.getElementById('ask-scroll-box')?.scrollTop || 0,
                  },
                });
              }}
            >
              {`See all ${post.comments_counts} comments`}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Meme;
